import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, MapPin, Users, Share2, CheckCircle, Clock,
  Copy, Loader2, Info, Wallet, ExternalLink, X, ShieldCheck, AlertTriangle, LogOut
} from "lucide-react";
import { Drawer } from "vaul";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { api, publicApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { openWhatsAppShare } from "../lib/whatsapp";
import { buildUpiLink } from "../lib/upi";

const formatDateRange = (s, e) => {
  const start = new Date(s);
  const end = new Date(e);
  const opts = { day: "numeric", month: "short" };
  return `${start.toLocaleDateString("en-IN", opts)} – ${end.toLocaleDateString("en-IN", { ...opts, year: "numeric" })}`;
};

const maskPhone = (p) => (p && p.length >= 5 ? p.slice(0, 5) + "***" : p);

export default function TripPage() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user: firebaseUser, profile, loading: authLoading } = useAuth();

  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJoinDrawer, setShowJoinDrawer] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [joining, setJoining] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [upiCopied, setUpiCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tripRes, partRes] = await Promise.all([
        publicApi.get(`/trips/${tripId}`),
        publicApi.get(`/trips/${tripId}/participants`),
      ]);
      setTrip(tripRes.data);
      setParticipants(partRes.data);
    } catch (err) {
      toast.error("Failed to load trip");
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Trip not found</p>
      </div>
    );
  }

  // Derived state
  const myUid = profile?.user_id || firebaseUser?.uid;
  const me = myUid ? participants.find((p) => p.user_id === myUid) : null;
  const isHost = !!myUid && trip.host_user_id === myUid;
  const hasJoined = !!me;
  const myStatus = me?.status; // 'confirmed' | 'pending' | 'paid_claimed' | 'rejected'

  const confirmedCount = participants.filter((p) => p.status === "confirmed").length;
  const spotsLeft = Math.max(0, trip.max_members - participants.length);
  const isFull = participants.length >= trip.max_members;

  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.is_host) return -1;
    if (b.is_host) return 1;
    return 0;
  });

  const pendingClaims = sortedParticipants.filter(
    (p) => !p.is_host && p.status === "paid_claimed"
  );

  const upiLink = buildUpiLink({
    upi_id: trip.host_upi_id,
    host_name: trip.host_name,
    amount: trip.advance_amount,
    trip_name: trip.trip_name,
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: trip.trip_name,
        text: `🔒 ${trip.trip_name} — lock your spot`,
        url: window.location.href,
      });
    } else {
      openWhatsAppShare(trip, window.location.href);
    }
  };

  const handleWhatsApp = () => openWhatsAppShare(trip, window.location.href);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const requireAuth = () => {
    if (!firebaseUser) {
      toast.info("Login first to join this trip");
      navigate("/login", { state: { from: { pathname: `/trip/${tripId}` } } });
      return false;
    }
    return true;
  };

  const handleJoin = async () => {
    if (!requireAuth()) return;
    if (!termsAgreed) { toast.error("Please agree to the terms"); return; }
    setJoining(true);
    try {
      await api.post(`/trips/${tripId}/join`, { terms_agreed: true });
      toast.success("You're in! Now pay to lock your seat.");
      setShowJoinDrawer(false);
      setTermsAgreed(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join");
    } finally {
      setJoining(false);
    }
  };

  const handleCopyUpi = () => {
    navigator.clipboard.writeText(trip.host_upi_id);
    setUpiCopied(true);
    toast.success("UPI ID copied");
    setTimeout(() => setUpiCopied(false), 2000);
  };

  const handleClaimPaid = async () => {
    if (!requireAuth()) return;
    setClaiming(true);
    try {
      await api.post(`/trips/${tripId}/claim-payment`);
      toast.success("Marked as paid. Waiting for host to confirm.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record claim");
    } finally {
      setClaiming(false);
    }
  };

  const handleHostAction = async (participantId, action) => {
    setConfirmingId(participantId + ":" + action);
    try {
      await api.post(
        `/trips/${tripId}/confirm-payment?participant_id=${participantId}&action=${action}`
      );
      toast.success(action === "confirm" ? "Participant confirmed" : "Payment rejected");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setConfirmingId(null);
    }
  };

  // ----- RENDER -----
  return (
    <div className="min-h-screen bg-slate-50 pb-40">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 pt-6 pb-20 px-4">
          <div className="flex items-center justify-between mb-6">
            <button
              data-testid="back-btn"
              onClick={() => navigate("/")}
              className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center active:scale-95 transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <button
                data-testid="share-btn"
                onClick={handleShare}
                className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center active:scale-95 transition-all"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
              {firebaseUser && (
                <button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="h-10 px-4 bg-white text-slate-700 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-sm hover:bg-slate-50 active:scale-95 transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              )}
            </div>
          </div>
          <div className="text-white">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-medium">{trip.destination}</span>
            </div>
            <h1 className="text-3xl font-bold mb-2" data-testid="trip-name">{trip.trip_name}</h1>
            <div className="text-white/90 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDateRange(trip.start_date, trip.end_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Hosted by {trip.host_name || "Host"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="px-4 -mt-12 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
              <div className="text-2xl font-bold text-slate-900">₹{trip.cost_per_person}</div>
              <div className="text-sm text-slate-500 mt-1">Per Person</div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
              <div className="text-2xl font-bold text-emerald-500">₹{trip.advance_amount}</div>
              <div className="text-sm text-slate-500 mt-1">Seat Lock Amount</div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
              <div className="text-lg font-bold text-slate-700">
                {participants.length} joined • {confirmedCount} locked in
              </div>
              <div className="text-sm text-slate-500 mt-1">{trip.max_members} total slots</div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
              <div className="text-lg font-bold text-slate-700">Min {trip.minimum_travelers}</div>
              <div className="text-sm text-slate-500 mt-1">to confirm trip</div>
            </div>
          </div>

          {spotsLeft <= 3 && spotsLeft > 0 && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-xl p-3">
              <p className="text-sm font-semibold text-orange-700">
                ⚡ Only {spotsLeft} {spotsLeft === 1 ? "spot" : "spots"} left
              </p>
            </div>
          )}

          {/* Trip Progress */}
          <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-4" data-testid="trip-progress">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-semibold text-slate-900">Trip Progress</h3>
              <span className="text-sm font-medium text-slate-700">
                {Math.min(confirmedCount, trip.minimum_travelers)}/{trip.minimum_travelers} locked in
              </span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${Math.min(100, Math.round((confirmedCount / Math.max(1, trip.minimum_travelers)) * 100))}%`
                }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Minimum travelers required to make the trip happen.
            </p>
          </div>
        </div>

        {/* HOST CONTROL PANEL */}
        {isHost && (
          <div className="px-4 mb-6" data-testid="host-panel">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <h2 className="text-lg font-semibold text-amber-900">Payment Verification</h2>
              </div>
              <p className="text-xs text-amber-800 mb-4">
                Verify payments before locking participants in. Check your UPI app.
              </p>

              {pendingClaims.length === 0 ? (
                <p className="text-sm text-amber-700 py-2" data-testid="no-pending-claims">
                  No pending payment claims right now.
                </p>
              ) : (
                <div className="space-y-2" data-testid="pending-claims-list">
                  {pendingClaims.map((p) => (
                    <div key={p.id} className="bg-white rounded-xl p-3 flex items-center justify-between border border-amber-100">
                      <div>
                        <div className="font-medium text-slate-900">{p.name || "Participant"}</div>
                        <div className="text-xs text-slate-500">{maskPhone(p.phone_number)}</div>
                        {p.claimed_payment_at && (
                          <div className="text-xs text-amber-700 mt-1">
                            Claimed {new Date(p.claimed_payment_at).toLocaleString("en-IN")}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid={`reject-${p.id}`}
                          disabled={!!confirmingId}
                          onClick={() => handleHostAction(p.id, "reject")}
                          className="px-3 h-9 rounded-full bg-white border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50"
                        >
                          Reject
                        </button>
                        <button
                          data-testid={`confirm-${p.id}`}
                          disabled={!!confirmingId}
                          onClick={() => handleHostAction(p.id, "confirm")}
                          className="px-3 h-9 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="px-4 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">
            Participants ({participants.length})
          </h2>
          {participants.length === 0 ? (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Nobody has committed yet. Start the momentum 🚀</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedParticipants.map((p) => (
                <ParticipantRow key={p.id} p={p} />
              ))}
            </div>
          )}
        </div>

        {/* Cancellation policy */}
        <div className="px-4 mb-6">
          <div className="bg-slate-100 rounded-xl p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Cancellation Policy</div>
            <div className="text-sm font-medium text-slate-700">
              {trip.cancellation_policy === "non_refundable" && "Non-refundable"}
              {trip.cancellation_policy === "50_percent" && "50% refundable"}
              {trip.cancellation_policy === "fully_refundable" && "Fully refundable"}
            </div>
          </div>
        </div>

        {/* STICKY CTA */}
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-200 shadow-lg p-4">
          <div className="max-w-md mx-auto">
            {!firebaseUser ? (
              <button
                data-testid="login-to-join-btn"
                onClick={() => navigate("/login", { state: { from: { pathname: `/trip/${tripId}` } } })}
                className="w-full bg-indigo-600 text-white hover:bg-indigo-700 rounded-full h-14 font-semibold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
              >
                Login to Join Trip
              </button>
            ) : isHost ? (
              <button
                data-testid="host-whatsapp-btn"
                onClick={handleWhatsApp}
                className="w-full bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full h-14 font-semibold shadow-lg shadow-green-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Share2 className="w-5 h-5" /> Invite More Friends on WhatsApp
              </button>
            ) : isFull && !hasJoined ? (
              <button disabled className="w-full bg-slate-300 text-slate-600 rounded-full h-14 font-semibold cursor-not-allowed">
                Trip Full 🚫
              </button>
            ) : !hasJoined ? (
              <button
                data-testid="join-trip-btn"
                onClick={() => setShowJoinDrawer(true)}
                className="w-full bg-indigo-600 text-white hover:bg-indigo-700 rounded-full h-14 font-semibold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
              >
                Lock My Spot
              </button>
            ) : myStatus === "confirmed" ? (
              <div className="space-y-3" data-testid="confirmed-cta">
                <div className="w-full bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                  <p className="font-semibold text-emerald-900">Your trip spot is locked in ✅</p>
                </div>
                <button
                  data-testid="invite-whatsapp-btn"
                  onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full h-12 px-6 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-green-500/20 active:scale-95 transition-all"
                >
                  <Share2 className="w-5 h-5" /> Invite Friends on WhatsApp
                </button>
              </div>
            ) : myStatus === "paid_claimed" ? (
              <div className="space-y-3" data-testid="awaiting-cta">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                  <Clock className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <p className="font-semibold text-amber-900">Awaiting host confirmation</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Your seat is not confirmed until host verifies your payment.
                  </p>
                </div>
              </div>
            ) : myStatus === "rejected" ? (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-center" data-testid="rejected-cta">
                <AlertTriangle className="w-6 h-6 text-rose-600 mx-auto mb-1" />
                <p className="font-semibold text-rose-900">Payment rejected by host</p>
                <p className="text-xs text-rose-700 mt-1">Reach out to the host directly to resolve.</p>
              </div>
            ) : (
              // myStatus === 'pending' — needs to pay
              <UpiPaymentBlock
                trip={trip}
                upiLink={upiLink}
                upiCopied={upiCopied}
                onCopyUpi={handleCopyUpi}
                claiming={claiming}
                onClaimPaid={handleClaimPaid}
              />
            )}

            <div className="mt-4 pt-3 border-t border-slate-200 flex justify-center">
              <button
                onClick={() => setShowAbout(true)}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                About TripLatch
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Join drawer */}
      <Drawer.Root open={showJoinDrawer} onOpenChange={setShowJoinDrawer}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl outline-none">
            <div className="max-w-md mx-auto px-4 pb-8 pt-4">
              <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">Lock your seat</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                <p className="text-base font-semibold text-blue-900 mb-2">
                  ₹{trip.advance_amount} is paid directly to the host via UPI.
                </p>
                <p className="text-sm text-blue-800 mb-3 leading-relaxed">
                  Your seat gets locked only after the host confirms payment.
                </p>
                <p className="text-xs text-blue-700 leading-relaxed">
                  TripLatch never holds money. Only join trips hosted by people you trust.
                </p>
              </div>

              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input
                  data-testid="terms-checkbox"
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-indigo-600"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  I understand TripLatch is a coordination platform only — payments go directly to the host.
                  I&rsquo;ve read the trip&rsquo;s cancellation policy and join only if I&rsquo;m serious about coming.
                </span>
              </label>

              <button
                data-testid="join-submit-btn"
                onClick={handleJoin}
                disabled={joining || !termsAgreed}
                className="w-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 rounded-full h-12 font-semibold active:scale-95 transition-all"
              >
                {joining ? "Locking..." : "Lock My Spot"}
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* About modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAbout(false)}>
          <div className="bg-white rounded-3xl max-w-md w-full p-8 animate-in" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowAbout(false)} className="absolute top-4 right-4">
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Why TripLatch?</h2>
            </div>
            <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
              <p>Group trips usually fail before they even begin.</p>
              <p>Too many maybe&rsquo;s. Too many last-minute cancellations.</p>
              <p className="font-semibold text-slate-900">
                TripLatch helps trip hosts turn &ldquo;we should go sometime&rdquo; into committed plans people actually show up for.
              </p>
              <p className="text-xs text-slate-500 pt-2 border-t border-slate-100 mt-3">
                We&rsquo;re a coordination platform. We never hold or process money.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ p }) {
  const status = p.status;
  const isHost = p.is_host;

  let badge;
  if (isHost || status === "confirmed") {
    badge = (
      <div className="flex items-center gap-1 text-emerald-600">
        <CheckCircle className="w-4 h-4" />
        <span className="text-xs font-semibold">Locked in</span>
      </div>
    );
  } else if (status === "paid_claimed") {
    badge = (
      <div className="flex items-center gap-1 text-amber-600">
        <Clock className="w-4 h-4" />
        <span className="text-xs font-semibold">Awaiting host</span>
      </div>
    );
  } else if (status === "rejected") {
    badge = (
      <div className="flex items-center gap-1 text-rose-500">
        <X className="w-4 h-4" />
        <span className="text-xs font-semibold">Rejected</span>
      </div>
    );
  } else {
    badge = (
      <div className="flex items-center gap-1 text-slate-500">
        <Clock className="w-4 h-4" />
        <span className="text-xs font-semibold">Unpaid</span>
      </div>
    );
  }

  return (
    <div data-testid={`participant-${p.id}`} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
          <span className="text-sm font-semibold text-indigo-600">
            {(p.name || "T").charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <div className="font-medium text-slate-900 flex items-center gap-2">
            {p.name || "Traveler"}
            {isHost && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Host</span>
            )}
          </div>
          <div className="text-xs text-slate-500">{maskPhone(p.phone_number)}</div>
        </div>
      </div>
      {badge}
    </div>
  );
}

function UpiPaymentBlock({ trip, upiLink, upiCopied, onCopyUpi, claiming, onClaimPaid }) {
  return (
    <div className="space-y-3" data-testid="upi-block">
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-5 h-5 text-emerald-700" />
          <h3 className="font-semibold text-emerald-900">Pay ₹{trip.advance_amount} to {trip.host_name || "Host"}</h3>
        </div>

        <div className="bg-white border border-emerald-100 rounded-xl p-3 mb-3">
          <div className="text-xs text-slate-500 mb-1">Host UPI ID</div>
          <div className="flex items-center justify-between gap-2">
            <code data-testid="host-upi-display" className="text-sm font-mono text-slate-900 break-all">
              {trip.host_upi_id}
            </code>
            <button
              data-testid="copy-upi-btn"
              onClick={onCopyUpi}
              className="flex-shrink-0 px-3 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium flex items-center gap-1"
            >
              {upiCopied ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              {upiCopied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <a
          data-testid="open-upi-app-btn"
          href={upiLink}
          className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-full h-12 px-6 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
        >
          <ExternalLink className="w-5 h-5" /> Open UPI App
        </a>
        <p className="text-xs text-emerald-800 text-center mt-2">
          Works with GPay, PhonePe, Paytm, BHIM
        </p>
        <div className="mt-3 pt-3 border-t border-emerald-100 space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <span>Direct UPI payment to host</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <span>Host manually verifies payments</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <span>No hidden TripLatch fees</span>
          </div>
        </div>
      </div>

      <button
        data-testid="i-have-paid-btn"
        onClick={onClaimPaid}
        disabled={claiming}
        className="w-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 rounded-full h-14 font-semibold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
      >
        {claiming ? "Submitting..." : "I Have Paid"}
      </button>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="text-xs text-amber-900 leading-relaxed">
          ⚠️ Your seat is not confirmed until host verifies your payment.
          TripLatch is a coordination platform — we never hold money.
        </p>
      </div>
    </div>
  );
}
